import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {
  Card,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import css from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/edit-education-card.css';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import * as reduxApi from 'src/utils/redux-api-v2';
import {useSelector, useDispatch} from 'react-redux';
import {selectApplicantInformation} from 'src/selectors/crm';


type ActionType = 'ADD' | 'EDIT' | 'DELETE';

export type educationType = {
  educationOrg: string;
  degree: string;
  major: string;
  grade: string;
  startYear: string;
  endYear: string;
};

type EducationCardProps = {
  setAddEducationCardOpen: (open: boolean) => void;
  educations: Array<educationType>;
  setEducations: (educations: Array<educationType>) => void;
  editIndex?: number;
};

const range = (from: number, to: number): Array<number> =>
  Array.from({length: to - from + 1}, (_, i) => from + i);
const years = range(1969, new Date().getFullYear() + 1);

export const EducationCard = ({
  setAddEducationCardOpen,
  educations = [],
  setEducations,
  editIndex,
}: EducationCardProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [isLoading, setIsLoading] = React.useState(false);
  const dispatch = useDispatch();

  // Get education data if editing
  const currentEducation = editIndex != null ? educations[editIndex] : null;

  // Set initial values from education if editing, otherwise use empty values
  const initialValues: educationType = {
    educationOrg: currentEducation?.educationOrg ?? '',
    degree: currentEducation?.degree ?? '',
    major: currentEducation?.major ?? '',
    grade: currentEducation?.grade ?? '',
    startYear: currentEducation?.startYear ?? '',
    endYear: currentEducation?.endYear ?? '',
  };

  const [formData, setFormData] = React.useState<educationType>(initialValues);

  const handleInputChange = (field: string, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    const {educationOrg, degree, major, startYear, endYear} = formData;
    return (
      educationOrg.trim() &&
      degree.trim() &&
      major.trim() &&
      startYear &&
      endYear
    );
  };

  const applicantDetails = useSelector(selectApplicantInformation);

  const handleFormSubmit = async (type: ActionType) => {
    if (!isFormValid() && type !== 'DELETE') {
      return;
    }

    const submitData = {
      section: 'EDUCATION',
      editFields: {
        data: type === 'DELETE' ? {} : formData,
        type,
        index: editIndex,
      },
    } as const;

    try {
      setIsLoading(true);
      await submitDataToAPI(submitData);

      switch (type) {
        case 'ADD':
          setEducations([...educations, formData]);
          break;

        case 'EDIT':
          if (editIndex != null) {
            const updatedEducations = [...educations];
            updatedEducations[editIndex] = formData;
            setEducations(updatedEducations);
          }
          break;

        case 'DELETE':
          if (editIndex != null) {
            const filteredEducations = educations.filter(
              (_, index) => index !== editIndex,
            );
            setEducations(filteredEducations);
          }
          break;
      }

      setAddEducationCardOpen(false);
    } catch (err: any) {
      console.error('Unable to submit form');
    } finally {
      setIsLoading(false);
    }
  };

  // @ts-expect-error - TS7006 - Parameter 'submitData' implicitly has an 'any' type.
  const submitDataToAPI = async (submitData) => {
    if (!applicantDetails?.id) {
      return;
    }
    await dispatch(
      reduxApi.post(
        `api/candidates/${applicantDetails.id}`,
        submitData,
        {},
        {
          apiPath: '/trmapi/',
        },
      ),
    );
  };

  const inputFields = [
    {
      field: 'educationOrg',
      label: t('Institution', 'Institution'),
      placeholder: t('Enter_Institution', 'Enter Institution'),
    },
    {
      field: 'degree',
      label: t('Degree', 'Degree'),
      placeholder: t('Enter_your_degree', 'Enter your degree'),
    },
    {
      field: 'major',
      label: t('DepartmentMajor', 'Department/Major'),
      placeholder: t('Enter_DepartmentMajor', 'Enter Department/Major'),
    },
    {
      field: 'grade',
      label: t('Grade', 'Grade'),
      placeholder: t('Enter_your_Grade', 'Enter your Grade'),
    },
  ];

  return (
    <Card classNames={{wrapper: css.cardWrapper}}>
      {inputFields.map(({field, label, placeholder}) => (
        <Input
          key={field}
          label={label}
          placeholder={placeholder}
          required
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'educationType'.
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          disabled={isLoading}
        />
      ))}
      <div className={css.twoInputContainer}>
        <SimpleDropdown
          label={t('From', 'From')}
          placeholder={t('years', 'years')}
          options={years.map((year) => ({
            key: year.toString(),
            label: year.toString(),
          }))}
          classNames={{wrapper: css.maxExpInput}}
          selectedKeys={formData.startYear ? [formData.startYear] : []}
          onChange={(option) => handleInputChange('startYear', option.key)}
          disabled={isLoading}
        />
        <SimpleDropdown
          label={t('To', 'To')}
          placeholder={t('years', 'years')}
          options={years.map((year) => ({
            key: year.toString(),
            label: year.toString(),
          }))}
          classNames={{wrapper: css.maxExpInput}}
          selectedKeys={formData.endYear ? [formData.endYear] : []}
          onChange={(option) => handleInputChange('endYear', option.key)}
          disabled={isLoading}
        />
      </div>
      <CardFooter>
        <div className={css.buttonContainer}>
          <div className={css.buttonWrapper}>
            {editIndex !== undefined && (
              <Button
                iconRightName="trash"
                classNames={{
                  wrapper: css.trashButton,
                  icon: css.trashIcon,
                }}
                onClick={() => handleFormSubmit('DELETE')}
                disabled={isLoading}
              />
            )}
          </div>
          <div>
            <Button
              type="ghost"
              onClick={() => setAddEducationCardOpen(false)}
              disabled={isLoading}
            >
              {t('Cancel', 'Cancel')}
            </Button>
            <Button
              type="primary"
              onClick={() =>
                handleFormSubmit(editIndex != null ? 'EDIT' : 'ADD')
              }
              disabled={isLoading || !isFormValid()}
            >
              {t('Apply', 'Apply')}
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
